import { Component, OnInit, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable, filter } from 'rxjs';
import { SearchEngineService } from '../services/search-engine.service';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ContentfulService } from '../services/contentful.service';
import { BreadcrumbDataService } from '../services/breadcrumb-data.service';
import { InvestorPortfolioSeriesReportingHoldingAssetBreadcrumb } from '../models/classes/investor-portfolio-series-reporting-holding-asset-breadcrumb.class';
import { ApiService } from '../core/http/api.service';
import { AssetDashboard, BaseAssetTypeDetails } from '../models/classes/asset-dashboard.class';
import { UserService } from '../services/user.service';
import { SnapshotService } from '../services/snapshot.service';
import { AssetMeta } from '../models/classes/asset-meta.class';
import { LoggingService } from '../services/logging.service';
import { NgTourService } from '../services/ng-tour-ui.service';
import { NgTourStepsService } from '../services/ng-tour-steps.service';
import { AssetSummaryContentful } from '../models/classes/asset-summary-contentful.class';
import { ActivityTrackingService } from '../services/activity-tracking.service';

import { NoAccessErrorComponent } from '../shared/no-access-error/no-access-error.component';
import { MatIconModule } from '@angular/material/icon';
import { AutoAdjustingDropdownComponent } from '../auto-adjusting-dropdown/auto-adjusting-dropdown.component';
import { DataUnavailableCardComponent } from '../shared/data-unavailable-card/data-unavailable-card.component';
import { AssetSummaryComponent } from './asset-summary/asset-summary.component';
import { FinancialSummaryComponent } from './financial-summary/financial-summary.component';
import { MapWidgetComponent } from '../shared/map-widget/map-widget.component';
import { GcmLoadingService } from '@gcm/gcm-ui-angular';
import { DateFormatPipe } from '../pipes/date-format.pipe';
import { ErrorMessageService } from '../services/error-message.service';
import { Breadcrumb } from '../models/interfaces/breadcrumb.interface';
import { parent } from '../models/classes/metadata.class';
import { AssetSummaryResponse } from '../models/interfaces/contentful-response.interface';

@Component({
  selector: 'app-asset-dashboard',
  imports: [
    NoAccessErrorComponent,
    MatIconModule,
    AutoAdjustingDropdownComponent,
    DataUnavailableCardComponent,
    RouterModule,
    AssetSummaryComponent,
    FinancialSummaryComponent,
    MapWidgetComponent
],
  templateUrl: './asset-dashboard.component.html',
  styleUrls: ['./asset-dashboard.component.scss']
})
export class AssetDashboardComponent implements OnInit {
  private seoService = inject(SearchEngineService);
  private activeRoute = inject(ActivatedRoute);
  private contentfulService = inject(ContentfulService);
  breadcrumbDataService = inject(BreadcrumbDataService);
  private router = inject(Router);
  private apiService = inject(ApiService);
  private userService = inject(UserService);
  private snapService = inject(SnapshotService);
  private loadingService = inject(GcmLoadingService);
  private loggingService = inject(LoggingService);
  private ngTourSteps = inject(NgTourStepsService);
  private ngTourUi = inject(NgTourService);
  activityTracking = inject(ActivityTrackingService);
  private dateFormat = inject(DateFormatPipe);
  private errorMessageService = inject(ErrorMessageService);

  readonly investorId = input.required<number>();
  readonly seriesId = input.required<number>();
  readonly holdingId = input<number>();
  readonly dealId = input<number>();
  readonly positionId = computed(() => this.dealId() ?? this.holdingId());
  readonly assetId = input.required<number>();
  readonly selectedAssetId = signal<number | null>(null);
  selectedAsOfDate = '';
  reportingPeriod = '';
  readonly iconSet = toSignal(
    this.contentfulService.getIconSetValue().pipe(filter((v): v is Record<string, string> => !!v)),
    { initialValue: {} as Record<string, string> }
  );
  hasError = false;
  errorMessage = '';
  parametersErrorMessage = '';
  breadcrumbs: Breadcrumb[] = [];
  assetsDataForBreadcrumb: InvestorPortfolioSeriesReportingHoldingAssetBreadcrumb[] = [];
  assetName = signal<string>('');
  asOfDropdownData: string[] = [];
  assetType = '';
  financialData: Record<string, string | number> = {};
  assetData = signal<BaseAssetTypeDetails | undefined>(undefined);
  showNoAccessError = false;
  assetSummary = signal<AssetSummaryContentful>(new AssetSummaryContentful());
  showAssetNotFound = false;
  firstTimeLoadingAssets = true;
  isAssetDataUnavailable = false;
  isDashboardApiError = false;
  parentData!: parent;

  constructor() {
    const ngTourSteps = this.ngTourSteps;

    this.ngTourSteps.getContentfulDataFetchedValue().subscribe((data: boolean) => {
      if (data) this.ngTourUi.setTourSteps(ngTourSteps.assetDashboardSteps);
    })
  }

  ngOnInit(): void {
    this.reportingPeriod = this.router.parseUrl(this.router.url).queryParams['reportingdate'];
    this.selectedAssetId.set(this.assetId());
    this.showNoAccessError = false;
    this.seoService.setCanonicalURL();
    this.checkIfUserIsExternal(this.investorId());
    this.fetchCommonAssets();
  }

  checkIfUserIsExternal(queryParamsInvestorId: string | number) {
    if (this.userService.isExternalUser()) {
      this.checkExternalUserAccess(queryParamsInvestorId);
    } else {
      this.getAssetDashboardDetails();
    }
  }

  checkExternalUserAccess(queryParamsInvestorId: string | number) {
    const assignedInvestorIds = this.userService.getInvestorIds();
    if (!assignedInvestorIds.includes(Number(queryParamsInvestorId))) {
      this.hasError = true;
      this.showNoAccessError = true;
      return;
    } else {
      this.hasError = false;
      this.getAssetDashboardDetails();
    }
  }

  getAssetDashboardDetails() {
    this.showAssetNotFound = false;
    const assetId = this.selectedAssetId();
    
    this.fetchDashboardDataFromApi(this.seriesId(), this.positionId(), assetId ?? 0, this.reportingPeriod).then((data: AssetDashboard | undefined) => {

      this.getBreadcrumbDropdownData(this.investorId());
      this.assetData.set(data?.summary);
      const currentAssetData = this.assetData();
      this.assetType = currentAssetData?.assetType ?? '';

      const summary = this.assetSummary();
      summary.summary = currentAssetData?.businessDescription ?? '';
      const website = currentAssetData?.website ?? '';
      summary.assetURL = (website && website != '') ? ((/(http(s?)):\/\//i.test(website)) ? website : "https://" + website) : '';
      summary.showAssetUrl = summary.assetURL? true : false;

      this.assetSummary.update(() => ({...summary}));
    });
  }

  async fetchDashboardDataFromApi(seriesId: number, positionId: number | undefined, assetId: number | null, reportingDate: string): Promise<AssetDashboard | undefined> {
    const loadingTask = 'fetchDashboardDataFromApi';
    this.loadingService.addAsyncTask(loadingTask);
    this.isDashboardApiError = false;
    try{
      return await this.apiService.getAssetDashboardDetails(this.investorId(), seriesId, positionId ?? 0, assetId ?? 0, reportingDate);
    }
    catch(err){
      this.hasError = true;
      this.isDashboardApiError = true;
      const errObj = err as Record<string, unknown>;
      if (errObj && errObj['error']) {
            const errDetail = errObj['error'] as Record<string, unknown>;
            const errors = errDetail['errors'] as unknown[];
            if (errors?.length > 0)  {
              const firstError = errors[0] as Record<string, unknown>;
              if ((firstError['message'] as string)?.includes('User does not have access')) {
                this.isDashboardApiError = true;
                this.getBreadcrumbDropdownData(this.investorId());
              }
            }
          }
          this.errorMessage = this.errorMessageService.getErrorMessage(err);
    }
    finally{
      this.loadingService.resolveAsyncTask(loadingTask);
    }
    return undefined;
  }

  fetchCommonAssets() {
    const assetId = this.selectedAssetId();

    (this.contentfulService.fetchCommonAssets('assetSummary', (assetId ?? 0).toString()) as Observable<AssetSummaryResponse>).subscribe((res) => {
      const summary = this.assetSummary();
      if (res.data.assetDetailsCollection.items.length === 0) {
        summary.logo = '';
        summary.slidingImgs = [];
      }
      else {

        if (res.data.assetDetailsCollection.items[0]?.map?.lat === undefined) {
          summary.showMap = false;
        }
        else {
          summary.showMap = true;
          summary.position = {
            lat: res.data.assetDetailsCollection.items[0].map.lat,
            lng: res.data.assetDetailsCollection.items[0].map.lon
          };
        }

        if (!res.data.assetDetailsCollection.items[0].logo) {
          summary.logo = '';
        }
        else {
          summary.logo = res.data.assetDetailsCollection.items[0].logo.url;
        }

        if (res.data.assetDetailsCollection.items[0].photosCollection.items.length !== 0) {
          summary.slidingImgs = [];
          for (let ssind = 0; ssind < res.data.assetDetailsCollection.items[0].photosCollection.items.length; ssind++) {
            if (res.data.assetDetailsCollection.items[0].photosCollection.items[ssind]) {
              summary.slidingImgs[ssind] = { src: res.data.assetDetailsCollection.items[0].photosCollection.items[ssind].url };
            }
          }
        } else {
          summary.slidingImgs = [];
        }
      }
      this.assetSummary.update(() => ({...summary}));
    });
    
  }

  getBreadcrumbDropdownData(queryParamsInvestorId: number) {
    const seriesId = this.seriesId();
    if (seriesId && queryParamsInvestorId > 0) {
      const loadingTask = 'getBreadcrumbDropdownData';
      this.loadingService.addAsyncTask(loadingTask);
      this.apiService.getAssetDashboardMetadata(seriesId, this.positionId() as number, this.selectedAssetId() ?? 0).subscribe({
        next: (data: AssetMeta) => {
          this.parentData = data.parent;
          this.updateBreadcrumbs(this.investorId(), this.parentData)
          if (this.firstTimeLoadingAssets) {
            this.assetsDataForBreadcrumb = data.relatedEntities;
            this.firstTimeLoadingAssets = false;
          }
          this.assetName.set(this.breadcrumbDataService.getAssetName(this.assetsDataForBreadcrumb, this.selectedAssetId() ?? 0) ?? '');
          this.isAssetDataUnavailable = this.isDashboardApiError;
          this.loadingService.resolveAsyncTask(loadingTask);
        },
        error: (err) => {
          this.loggingService.log("----- Asset Dashboard Metadata API Exception -----", err);
          this.loadingService.resolveAsyncTask(loadingTask);
        }
      });
    }
  }

  updateBreadcrumbs(investorId: number, parentData: parent) {
    this.breadcrumbs = [];
    this.breadcrumbs = this.breadcrumbs.concat(this.breadcrumbDataService.getDashboardBreadcrumb(investorId));
    const seriesBreadcrumbs = this.breadcrumbDataService.getPortfolioSeriesBreadcrumbs(investorId, this.seriesId(), parentData.parent)
    this.breadcrumbs.push(seriesBreadcrumbs);
    const positionBreadcrumbs = this.breadcrumbDataService.getDealBreadcrumbs(investorId, this.seriesId(), this.positionId() as number, parentData);
    this.breadcrumbs.push(positionBreadcrumbs);
    this.breadcrumbs.forEach(breadcrumb => {
      breadcrumb.queryParams = this.breadcrumbDataService.getQueryParamsForBreadcrumbs(breadcrumb);
    });
  }

  goToSelectedAsset(assetId: string) {
    const assetIdNum = Number(assetId);
    this.assetsDataForBreadcrumb.map(asset => {
      if (asset.id == assetIdNum) {
        const selectedAsset = asset;
        const route = `/investor/${this.investorId()}/series/${this.seriesId()}/deal/${this.positionId()}/asset/${selectedAsset.id}`;
        this.router.navigate([route], {
          relativeTo: this.activeRoute,
          queryParamsHandling: 'merge'
        });
        this.selectedAssetId.set(selectedAsset.id);
        this.assetChanged();
        return;
      }
    });
  }

  assetChanged() {
    this.assetName.set(this.breadcrumbDataService.getAssetName(this.assetsDataForBreadcrumb, this.selectedAssetId() ?? 0) ?? '');
    this.assetSummary.set(new AssetSummaryContentful());
    this.getAssetDashboardDetails();
    this.fetchCommonAssets();
  }

  updateAsOfDate(date: string) {
    this.fetchDashboardDataFromApi(this.seriesId(), this.positionId(), this.selectedAssetId(), date).then((data: AssetDashboard | undefined) => {
      this.assetData.set(data?.summary);
      this.selectedAsOfDate = this.dateFormat.transform(date, "MM/dd/yyyy") ?? '';
      this.reportingPeriod = this.dateFormat.transform(date, "yyyy-MM-dd") ?? '';
      if (this.isAssetDataUnavailable) {
        this.getBreadcrumbDropdownData(this.investorId())
      }
      this.router.navigate(
        [], {
        relativeTo: this.activeRoute,
        queryParams: { reportingdate: date },
        queryParamsHandling: 'merge'
      }).then(() => {
        this.updateBreadcrumbs(this.investorId(), this.parentData);
      });
    });
  }

  getSnapshot() {
    const assetId = this.selectedAssetId() ?? 0;
    this.activityTracking.downloadSnapshot(assetId, this.assetName());
    const fileName = 'AssetSnapshot_' + this.assetName().replace(/\s/g, "_") + '_' + this.reportingPeriod + '.pdf'
    this.snapService.getSnapshot(this.reportingPeriod, fileName, this.investorId(), this.seriesId(), this.positionId(), assetId)
  }

  breadcrumbClick(breadcrumb: Breadcrumb) {
    this.activityTracking.navigateToDashboard(breadcrumb.type === 'Dashboard' ? 'Investor' : breadcrumb.type, breadcrumb, 'asset dashboard breadcrumbs');
  }
}
